<template>
	<div>
		<div class="mb-6 mt-2 border-b border-dark dark:border-light">
			<h3 class="mb-2">Configuration</h3>
		</div>

		<div class="mx-auto mb-2 w-full px-2 md:mb-5">
			<div class="formGroup withIcon">
				<label class="textGrey">Application</label>
				<select v-model="selectedApp" @change="handleAppChange">
					<option :value="null">-- Select Application --</option>
					<option v-for="app in supportedApps" :key="app" :value="app">
						{{ $global.prettyPrint(app) }}
					</option>
				</select>
				<span class="fa-light fa-chevron-down"></span>
			</div>
		</div>

		<template v-if="selectedApp">
			<div v-if="selectedApp === 'hangup'" class="mx-auto mb-2 w-full px-2 md:mb-5">
				<div class="formGroup withIcon">
					<label class="textGrey">Hangup Code (Optional, defaults to 200 OK)</label>
					<select @change="setAppConfig(parseInt($event.target.value), 'code')">
						<option :value="null" :selected="!appConfig[selectedApp]?.code">-- Select Code --</option>
						<option
							v-for="code in hangupCodes"
							:key="code.code"
							:value="code.code"
							:selected="appConfig[selectedApp]?.code === code.code"
						>
							{{ code.code }} - {{ code.description }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>
			<div
				v-if="['call_forwarding', 'prompt_then_forward'].includes(selectedApp)"
				class="mx-auto mb-2 w-full px-2 md:mb-5"
			>
				<div class="formGroup withIcon">
					<label class="textGrey">CLI Preference</label>
					<select @change="setAppConfig($event.target.value, 'cli_preference')">
						<option :value="-1">-- Select Application --</option>
						<option
							value="AS_RECEIVED"
							:selected="appConfig[selectedApp]?.cli_preference === 'AS_RECEIVED'"
						>
							Present Caller CLI
						</option>
						<option
							value="PHONE_NUMBER"
							:selected="appConfig[selectedApp]?.cli_preference === 'PHONE_NUMBER'"
						>
							Present Phone Number
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>

			<div v-if="selectedApp === 'flows'" class="mx-auto mb-2 w-full px-2 md:mb-5">
				<div class="formGroup withIcon">
					<div class="flex items-center gap-2">
						<label class="textGrey">Flow</label>
						<a
							v-if="appConfig?.flows?.flowId"
							class="flex items-center gap-1 rounded p-1 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
							:href="`/flows/${appConfig.flows.flowId}`"
							target="_blank"
							title="Open flow in new tab"
						>
							<i class="fa-light fa-arrow-up-right-from-square text-xs"></i>
						</a>
					</div>
					<select @change="setAppConfig($event.target.value, 'flowId')">
						<option :value="-1">-- Select Flow --</option>
						<option
							v-for="flow in accountFlows"
							:key="flow.id"
							:value="flow.id"
							:selected="parseInt(appConfig?.flows?.flowId) === flow.id"
						>
							{{ flow.name }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>

			<div v-if="selectedApp === 'sip_trunk'" class="mx-auto mb-2 w-full px-2 md:mb-5">
				<div class="formGroup withIcon">
					<label class="textGrey">SIP Trunk</label>
					<select @change="setAppConfig(parseInt($event.target.value), 'trunkId')">
						<option :value="-1" :selected="!appConfig?.sip_trunk?.trunkId">-- Select SIP Trunk --</option>
						<option
							v-for="trunk in accountSipTrunks"
							:key="trunk.id"
							:value="trunk.id"
							:selected="appConfig?.sip_trunk?.trunkId === trunk.id"
						>
							{{ trunk.username }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>

			<div v-if="selectedApp === 'mobile_sim'" class="mx-auto mb-2 w-full px-2 md:mb-5">
				<div class="formGroup withIcon">
					<div class="flex items-center gap-2">
						<label class="textGrey">Mobile SIM</label>
						<a
							v-if="appConfig?.mobile_sim?.simId"
							class="flex items-center gap-1 rounded p-1 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
							:href="`/mobilesims/${appConfig.mobile_sim.simId}`"
							target="_blank"
							title="Open SIM in new tab"
						>
							<i class="fa-light fa-arrow-up-right-from-square text-xs"></i>
						</a>
					</div>
					<select @change="setAppConfig(parseInt($event.target.value), 'simId')">
						<option :value="-1" :selected="!appConfig?.mobile_sim?.simId">-- Select Mobile SIM --</option>
						<option
							v-for="sim in accountMobileSims"
							:key="sim.id"
							:value="sim.id"
							:selected="appConfig?.mobile_sim?.simId === sim.id"
						>
							{{ sim.number }}{{ sim.description ? ' - ' + sim.description : '' }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>

			<div
				v-if="selectedApp === 'mobile_sim' && appConfig?.mobile_sim?.simId"
				class="mb-2 flex w-full flex-row md:mb-5"
			>
				<form-field label="Voicemail Enabled?" class="px-2">
					<input
						type="checkbox"
						:checked="appConfig[selectedApp]?.voicemail?.enabled"
						class="tw-toggle ml-2"
						@change="
							setAppConfig(
								{ ...appConfig?.mobile_sim?.voicemail, enabled: $event.target.checked },
								'voicemail'
							)
						"
					/>
				</form-field>
			</div>

			<div
				v-if="
					selectedApp === 'mobile_sim' &&
					appConfig?.mobile_sim?.simId &&
					appConfig?.mobile_sim?.voicemail?.enabled
				"
				class="mx-auto mb-2 w-full px-2 md:mb-5"
			>
				<div class="formGroup withIcon">
					<label class="textGrey">Greeting (optional)</label>
					<select
						@change="
							setAppConfig(
								{ ...appConfig?.mobile_sim?.voicemail, greeting: $event.target.value },
								'voicemail'
							)
						"
					>
						<option :value="-1" :selected="!appConfig[selectedApp]?.voicemail?.greeting">
							-- Select File --
						</option>
						<option
							v-for="file in accountFiles"
							:key="file.id"
							:value="file.id"
							:selected="appConfig[selectedApp]?.voicemail?.greeting === file.id"
						>
							{{ file.name }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>

			<div
				v-if="
					selectedApp === 'mobile_sim' &&
					appConfig?.mobile_sim?.simId &&
					appConfig?.mobile_sim?.voicemail?.enabled
				"
				class="mb-2 flex w-full flex-row md:mb-5"
			>
				<form-field label="Skip Instructions?" class="w-1/2 px-2">
					<input
						type="checkbox"
						:checked="appConfig[selectedApp]?.voicemail?.skipInstructions"
						class="tw-toggle ml-2"
						@change="
							setAppConfig(
								{ ...appConfig?.mobile_sim?.voicemail, skipInstructions: $event.target.checked },
								'voicemail'
							)
						"
					/>
				</form-field>
			</div>

			<div
				v-if="
					selectedApp === 'mobile_sim' &&
					appConfig?.mobile_sim?.simId &&
					appConfig?.mobile_sim?.voicemail?.enabled
				"
				class="mx-auto mb-2 flex w-full flex-row flex-wrap md:mb-5"
			>
				<form-field label="Min Seconds" class="w-1/2 px-2">
					<input
						type="number"
						class="form-control"
						:value="appConfig[selectedApp]?.voicemail?.minSecs"
						@input="
							setAppConfig(
								{ ...appConfig?.mobile_sim?.voicemail, minSecs: $event.target.value },
								'voicemail'
							)
						"
					/>
				</form-field>
				<form-field label="Max Seconds" class="w-1/2 px-2">
					<input
						type="number"
						class="form-control"
						:value="appConfig[selectedApp]?.voicemail?.maxSecs"
						@input="
							setAppConfig(
								{ ...appConfig?.mobile_sim?.voicemail, maxSecs: $event.target.value },
								'voicemail'
							)
						"
					/>
				</form-field>
			</div>

			<div
				v-if="
					selectedApp === 'mobile_sim' &&
					appConfig?.mobile_sim?.simId &&
					appConfig?.mobile_sim?.voicemail?.enabled
				"
				class="mx-auto mb-2 flex w-full flex-row flex-wrap px-2 md:mb-5"
			>
				<form-field label="Email Alert" class="w-full">
					<input
						type="text"
						class="form-control"
						:value="appConfig[selectedApp]?.voicemail?.emailAlert"
						@input="
							setAppConfig(
								{ ...appConfig?.mobile_sim?.voicemail, emailAlert: $event.target.value },
								'voicemail'
							)
						"
					/>
				</form-field>
			</div>

			<div v-if="selectedApp === 'voicemail'" class="mx-auto mb-2 w-full px-2 md:mb-5">
				<div class="formGroup withIcon">
					<label class="textGrey">Greeting (optional)</label>
					<select @change="setAppConfig($event.target.value, 'greeting')">
						<option :value="-1" :selected="!appConfig[selectedApp]?.greeting">-- Select File --</option>
						<option
							v-for="file in accountFiles"
							:key="file.id"
							:value="file.id"
							:selected="appConfig[selectedApp]?.greeting === file.id"
						>
							{{ file.name }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>

			<div v-if="selectedApp === 'voicemail'" class="mb-2 flex w-full flex-row md:mb-5">
				<form-field label="Skip Instructions?" class="w-1/2 px-2">
					<input
						type="checkbox"
						class="tw-toggle ml-2"
						:checked="appConfig[selectedApp]?.skipInstructions"
						@change="setAppConfig($event.target.checked, 'skipIntructions')"
					/>
				</form-field>
			</div>

			<div v-if="selectedApp === 'voicemail'" class="mx-auto mb-2 flex w-full flex-row flex-wrap md:mb-5">
				<form-field label="Min Seconds" class="w-1/2 px-2">
					<input
						type="number"
						class="form-control"
						:value="appConfig[selectedApp]?.minSecs"
						@input="setAppConfig($event.target.value, 'minSecs')"
					/>
				</form-field>
				<form-field label="Max Seconds" class="w-1/2 px-2">
					<input
						type="number"
						class="form-control"
						:value="appConfig[selectedApp]?.maxSecs"
						@input="setAppConfig($event.target.value, 'maxSecs')"
					/>
				</form-field>
			</div>

			<div v-if="selectedApp === 'voicemail'" class="mx-auto mb-2 flex w-full flex-row flex-wrap px-2 md:mb-5">
				<form-field label="Email Alert" class="w-full">
					<input
						type="text"
						class="form-control"
						:value="appConfig[selectedApp]?.emailAlert"
						@input="setAppConfig($event.target.value, 'emailAlert')"
					/>
				</form-field>
			</div>

			<div
				v-if="['play_prompt', 'prompt_then_forward'].includes(selectedApp)"
				class="mx-auto mb-2 w-full px-2 md:mb-5"
			>
				<div class="formGroup withIcon">
					<label class="textGrey">Prompt File</label>
					<select @change="setAppConfig($event.target.value, 'fileId')">
						<option :value="-1" :selected="!appConfig[selectedApp]?.fileId">-- Select File --</option>
						<option
							v-for="file in accountFiles"
							:key="file.id"
							:value="file.id"
							:selected="appConfig[selectedApp]?.fileId === file.id"
						>
							{{ file.name }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>

			<div v-if="selectedApp === 'dial_thru'" class="mx-auto mb-2 w-full px-2 md:mb-5">
				<div class="formGroup withIcon">
					<label class="textGrey">Welcome Prompt</label>
					<select @change="setAppConfig($event.target.value, 'welcomePromptFileId')">
						<option :value="-1" :selected="!appConfig[selectedApp]?.welcomePromptFileId">
							-- Select File --
						</option>
						<option
							v-for="file in accountFiles"
							:key="file.id"
							:value="file.id"
							:selected="appConfig[selectedApp]?.welcomePromptFileId === file.id"
						>
							{{ file.name }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>

			<div
				v-if="['prompt_then_forward', 'call_forwarding'].includes(selectedApp)"
				class="mx-auto mb-2 w-full px-2 md:mb-5"
			>
				<div class="formGroup">
					<label class="textGrey">Destination Number</label>
					<input
						type="text"
						class="form-control"
						:value="appConfig[selectedApp]?.destination"
						@input="setAppConfig($event.target.value, 'destination')"
					/>
				</div>
			</div>

			<div
				v-if="['account_balance_check', 'tenant_balance_check', 'voicemail_manager'].includes(selectedApp)"
				class="mx-auto mb-2 w-full px-2 md:mb-5"
			>
				<div class="formGroup">
					<label class="textGrey">PIN Number (4-6 digits)</label>
					<input
						type="text"
						class="form-control"
						:value="appConfig[selectedApp]?.pin_number"
						@input="setAppConfig($event.target.value, 'pin_number')"
					/>
				</div>
			</div>
		</template>
	</div>
</template>

<script>
import flowsApi from '@/scripts/flows/flows.api'
import sipTrunksApi from '@/scripts/siptrunks/siptrunks.api'
import mobileSimsApi from '@/scripts/mobilesims/mobilesims.api'
import filesApi from '@/scripts/files/files.api'

export default {
	inject: ['userRoles'],
	props: {
		modelValue: {
			type: Object,
			required: true
		},
		tenants: {
			type: Array,
			default: () => []
		},
		account: {
			type: Object,
			default: null
		},
		showAllApps: {
			type: Boolean,
			default: false
		}
	},
	emits: ['update:modelValue'],
	data() {
		return {
			flows: [],
			sipTrunks: [],
			mobileSims: [],
			files: [],
			selectedApp: this.modelValue?.app || null,
			appList: [
				'play_prompt',
				'speaking_clock',
				'diagnostics',
				'tenant_balance_check',
				'voicemail_manager',
				'bt_1471',
				'cli_readback',
				'hangup'
			],
			accountAppList: [
				'dial_thru',
				'account_balance_check',
				'call_forwarding',
				'proxy',
				'flows',
				'sip_trunk',
				'mobile_sim',
				'prompt_then_forward',
				'voicemail'
			],
			hangupCodes: [
				{ code: 200, description: 'OK' },
				{ code: 202, description: 'Accepted' },
				{ code: 204, description: 'No Notification' },
				{ code: 300, description: 'Multiple Choices' },
				{ code: 301, description: 'Moved Permanently' },
				{ code: 302, description: 'Moved Temporarily' },
				{ code: 305, description: 'Use Proxy' },
				{ code: 380, description: 'Alternative Service' },
				{ code: 400, description: 'Bad Request' },
				{ code: 401, description: 'Unauthorized' },
				{ code: 402, description: 'Payment Required' },
				{ code: 403, description: 'Forbidden' },
				{ code: 404, description: 'Not Found' },
				{ code: 405, description: 'Method Not Allowed' },
				{ code: 406, description: 'Not Acceptable' },
				{ code: 407, description: 'Proxy Authentication Required' },
				{ code: 408, description: 'Request Timeout' },
				{ code: 409, description: 'Conflict' },
				{ code: 410, description: 'Gone' },
				{ code: 411, description: 'Length Required' },
				{ code: 412, description: 'Conditional Request Failed' },
				{ code: 413, description: 'Request Entity Too Large' },
				{ code: 414, description: 'Request-URI Too Long' },
				{ code: 415, description: 'Unsupported Media Type' },
				{ code: 416, description: 'Unsupported URI Scheme' },
				{ code: 417, description: 'Unknown Resource-Priority' },
				{ code: 420, description: 'Bad Extension' },
				{ code: 421, description: 'Extension Required' },
				{ code: 422, description: 'Session Interval Too Small' },
				{ code: 423, description: 'Interval Too Brief' },
				{ code: 424, description: 'Bad Location Information' },
				{ code: 425, description: 'Bad Alert Message' },
				{ code: 428, description: 'Use Identity Header' },
				{ code: 429, description: 'Provide Referrer Identity' },
				{ code: 430, description: 'Flow Failed' },
				{ code: 433, description: 'Anonymity Disallowed' },
				{ code: 436, description: 'Bad Identity-Info' },
				{ code: 437, description: 'Unsupported Certificate' },
				{ code: 438, description: 'Invalid Identity Header' },
				{ code: 439, description: 'First Hop Lacks Outbound Support' },
				{ code: 440, description: 'Max-Breadth Exceeded' },
				{ code: 469, description: 'Bad Info Package' },
				{ code: 470, description: 'Consent Needed' },
				{ code: 480, description: 'Temporarily Unavailable' },
				{ code: 481, description: 'Call/Transaction Does Not Exist' },
				{ code: 482, description: 'Loop Detected' },
				{ code: 483, description: 'Too Many Hops' },
				{ code: 484, description: 'Address Incomplete' },
				{ code: 485, description: 'Ambiguous' },
				{ code: 486, description: 'Busy Here' },
				{ code: 487, description: 'Request Terminated' },
				{ code: 488, description: 'Not Acceptable Here' },
				{ code: 489, description: 'Bad Event' },
				{ code: 491, description: 'Request Pending' },
				{ code: 493, description: 'Undecipherable' },
				{ code: 494, description: 'Security Agreement Required' },
				{ code: 500, description: 'Internal Server Error' },
				{ code: 501, description: 'Not Implemented' },
				{ code: 502, description: 'Bad Gateway' },
				{ code: 503, description: 'Service Unavailable' },
				{ code: 504, description: 'Server Time-out' },
				{ code: 505, description: 'Version Not Supported' },
				{ code: 513, description: 'Message Too Large' },
				{ code: 555, description: 'Push Notification Service Not Supported' },
				{ code: 580, description: 'Precondition Failure' },
				{ code: 600, description: 'Busy Everywhere' },
				{ code: 603, description: 'Decline' },
				{ code: 604, description: 'Does Not Exist Anywhere' },
				{ code: 606, description: 'Not Acceptable' },
				{ code: 607, description: 'Unwanted' },
				{ code: 608, description: 'Rejected' }
			]
		}
	},
	computed: {
		supportedApps() {
			if (this.showAllApps) {
				return [...this.appList, ...this.accountAppList].sort()
			}
			return (this.account ? [...this.appList, ...this.accountAppList] : this.appList).sort()
		},
		appConfig() {
			return this.modelValue?.appConfig || {}
		},
		accountFlows() {
			return this.flows
				.filter((f) => this.showAllApps || (this.account && f.account?.id === this.account?.id))
				.sort((a, b) => a.name.localeCompare(b.name))
		},
		accountFiles() {
			return this.files
				.filter(
					(f) =>
						this.showAllApps ||
						(this.account && f.account?.id === this.account?.id) ||
						(!f.account && f.tenant?.id === this.modelValue?.tenant?.id)
				)
				.sort((a, b) => a.name.localeCompare(b.name))
		},
		accountSipTrunks() {
			return this.sipTrunks
				.filter((t) => this.showAllApps || (this.account && t.account?.id === this.account?.id))
				.sort((a, b) => a.username.localeCompare(b.username))
		},
		accountMobileSims() {
			return this.mobileSims
				.filter((s) => this.showAllApps || (this.account && s.account?.id === this.account?.id))
				.sort((a, b) => a.number.localeCompare(b.number))
		}
	},
	watch: {
		account(newVal) {
			if (!newVal && this.accountAppList.includes(this.selectedApp)) {
				this.selectedApp = null
			}
		}
	},
	async created() {
		if (this.userRoles.includes('account'))
			this.appList = this.appList.filter((app) => app !== 'tenant_balance_check')

		await this.loadData()
	},
	methods: {
		async loadData() {
			try {
				const params = {}
				params.tenant = this.modelValue?.tenant?.id
				const [flowsResult, sipTrunksResult, mobileSimsResult, filesResult] = await Promise.all([
					flowsApi.getAll(params),
					sipTrunksApi.getAll(params),
					mobileSimsApi.getAll(params),
					filesApi.getAll(params)
				])

				this.flows = flowsResult.data.results
				this.sipTrunks = sipTrunksResult.data.results
				this.mobileSims = mobileSimsResult.data.results
				this.files = filesResult.data.results
			} catch (err) {
				console.error(err)
			}
		},
		handleAppChange() {
			this.$emit('update:modelValue', {
				...this.modelValue,
				app: this.selectedApp
			})
		},
		setAppConfig(value, key) {
			this.$emit('update:modelValue', {
				...this.modelValue,
				appConfig: {
					...this.modelValue.appConfig,
					[this.selectedApp]: {
						...this.modelValue.appConfig?.[this.selectedApp],
						[key]: value
					}
				}
			})
		}
	}
}
</script>
